import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { agentsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ShieldAlert, ArrowLeft, Users, UserPlus, Phone, MapPinned, Trash2, Loader2, UserCheck, Mail, Lock, Eye, EyeOff,
} from "lucide-react";

const empty = { first_name: "", last_name: "", phone: "", zone: "", email: "", password: "" };

const Team = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const load = async () => {
    try {
      setAgents(await agentsApi.list());
    } catch {
      toast.error("Impossible de charger les agents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim() || !form.zone.trim() || !form.email.trim() || !form.password.trim()) {
      return toast.error("Tous les champs sont obligatoires");
    }
    if (form.password.trim().length < 6) {
      return toast.error("Le mot de passe doit contenir au moins 6 caractères");
    }
    setSaving(true);
    try {
      await agentsApi.create(form);
      toast.success(`Agent ${form.first_name} ${form.last_name} ajouté`);
      setForm(empty);
      setShowPassword(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'ajout de l'agent");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    setRemoving(a.id);
    try {
      await agentsApi.remove(a.id);
      toast.success("Agent supprimé");
      setAgents((prev) => prev.filter((x) => x.id !== a.id));
    } catch {
      toast.error("Échec de la suppression");
    } finally {
      setRemoving("");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <header className="h-16 sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display font-bold leading-tight flex items-center gap-2"><Users className="w-4 h-4 text-yellow-400" /> Gestion d'équipe</h1>
            <p className="text-[11px] font-mono text-slate-500">Sama Jaamu — Agents d'intervention</p>
          </div>
        </div>
        <Button data-testid="team-back-btn" onClick={() => navigate("/")} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour au dashboard
        </Button>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Add form */}
        <section className="lg:col-span-2">
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 sticky top-24">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2 mb-5">
              <UserPlus className="w-5 h-5 text-emerald-400" /> Ajouter un agent
            </h2>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Prénom</Label>
                  <Input data-testid="agent-firstname-input" value={form.first_name} onChange={set("first_name")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Awa" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Nom</Label>
                  <Input data-testid="agent-lastname-input" value={form.last_name} onChange={set("last_name")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Diop" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Téléphone</Label>
                <Input data-testid="agent-phone-input" value={form.phone} onChange={set("phone")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="+221 77 123 45 67" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Zone d'intervention</Label>
                <Input data-testid="agent-zone-input" value={form.zone} onChange={set("zone")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Dakar-Plateau" />
              </div>

              <div className="pt-4 mt-1 border-t border-slate-800 space-y-4">
                <p className="text-[11px] font-mono uppercase tracking-wider text-emerald-400/80 flex items-center gap-1.5">
                  <Lock className="w-3 h-3" /> Identifiants application mobile
                </p>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Email de connexion</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input data-testid="agent-email-input" type="email" value={form.email} onChange={set("email")} className="pl-10 bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="awa.diop@samajaamu.sn" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input data-testid="agent-password-input" type={showPassword ? "text" : "password"} value={form.password} onChange={set("password")} className="pl-10 pr-10 bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Min. 6 caractères" />
                    <button type="button" data-testid="agent-toggle-password" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Masquer" : "Afficher"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-500 leading-relaxed">Ces identifiants permettront à l'agent de se connecter à l'application mobile Sama Jaamu.</p>
                </div>
              </div>
              <Button type="submit" data-testid="agent-save-btn" disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" /> Enregistrer l'agent</>}
              </Button>
            </form>
          </div>
        </section>

        {/* List */}
        <section className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold text-lg">Agents enregistrés</h2>
            <span className="font-mono text-xs text-slate-500" data-testid="agents-count">{agents.length} agent(s)</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : agents.length === 0 ? (
            <div className="bg-slate-900/60 border border-dashed border-slate-700 rounded-2xl p-10 text-center text-slate-500">
              <Users className="w-8 h-8 mx-auto mb-3 text-slate-600" />
              <p className="font-display font-semibold text-slate-300">Aucun agent enregistré</p>
              <p className="text-sm mt-1">Ajoutez votre premier agent d'intervention via le formulaire.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="agents-list">
              {agents.map((a) => (
                <div key={a.id} data-testid={`agent-row-${a.id}`} className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center shrink-0">
                      <UserCheck className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-display font-semibold text-slate-100 truncate">{a.first_name} {a.last_name}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs font-mono text-slate-400 mt-0.5">
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-emerald-500" /> {a.phone}</span>
                        <span className="flex items-center gap-1"><MapPinned className="w-3 h-3 text-yellow-500" /> {a.zone}</span>
                        {a.email && <span className="flex items-center gap-1" data-testid={`agent-email-${a.id}`}><Mail className="w-3 h-3 text-sky-500" /> {a.email}</span>}
                      </div>
                      {a.assignment ? (
                        <span className="inline-block mt-1.5 text-[10px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full">En intervention</span>
                      ) : (
                        <span className="inline-block mt-1.5 text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full">Disponible</span>
                      )}
                    </div>
                  </div>
                  <Button data-testid={`agent-delete-${a.id}`} onClick={() => remove(a)} disabled={removing === a.id} size="icon" variant="ghost" className="text-slate-400 hover:text-red-400 hover:bg-red-950/40 shrink-0">
                    {removing === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Team;
